import * as zod from 'zod';

import { isDynamicBase } from '@sb/integrations/utils';
import { BlendConfig, MotionKind, TCPOptionOrAuto } from '@sb/motion-planning';
import {
  ActionRequiredError,
  DistanceUnit,
  MotionSpeed,
  SpeedLimitOption,
  Step,
} from '@sb/remote-control/types';
import type { WaypointStepArguments } from '@sb/routine-runner';
import type { SpeedProfile } from '@sb/routine-runner/speed-profile';
import { Expression, Space } from '@sb/routine-runner/types';

import { calculateSpeedProfileFromStepMotionSpeed } from '../util/calculateSpeedProfileFromStepMotionSpeed';
import { buildConditional } from '../util/conditionalsBuilder';
import { getDistanceUnitInfo } from '../util/distance';
import convertJavaScriptExpressionToUser from '../util/expressions/convertJavaScriptExpressionToUser';
import { convertPythonExpression } from '../util/expressions/v2';
import { validateCodeExpression } from '../util/expressions/validateCodeExpression';
import { listAllSteps } from '../util/listAllSteps';
import { validateMotionSpeed } from '../util/validators';

/**
 * Check if pallet base dimensions are sufficient for the pallet boxes layer patterns
 */
function checkPalletBaseDimensions(
  palletBoxes: Space.PalletBoxes,
  palletBase: Space.PalletBase,
): boolean {
  if (palletBoxes.layerPatterns.length === 0) {
    return false;
  }

  const boxTypesMap = new Map(
    palletBoxes.boxTypes.map((boxType) => [boxType.id, boxType]),
  );

  let maxX = 0;
  let maxY = 0;

  for (const layerPattern of palletBoxes.layerPatterns) {
    const boxType = boxTypesMap.get(layerPattern.boxTypeID);
    if (!boxType) continue;

    for (const box of layerPattern.boxList) {
      const boxWidth =
        box.rotation === 0 || box.rotation === 180
          ? boxType.width
          : boxType.depth;

      const boxDepth =
        box.rotation === 0 || box.rotation === 180
          ? boxType.depth
          : boxType.width;

      const boxMaxX = box.x + boxWidth;
      const boxMaxY = box.y + boxDepth;

      maxX = Math.max(maxX, boxMaxX);
      maxY = Math.max(maxY, boxMaxY);
    }
  }

  return maxX > palletBase.widthMM || maxY > palletBase.lengthMM;
}

export namespace WaypointStep {
  export const name = 'Waypoint';
  export const description = 'Move the robot to a specific position in space';
  export const librarySection = Step.LibrarySection.Hidden;
  export const librarySort = '1'; // unused
  export const argumentKind = 'Waypoint';
  export const featureFlag = 'ros2';

  const permittedParentStepKinds = ['MoveArmToV2', 'Weld'];

  /**
   * Get the dynamic name for a waypoint step based on its configuration
   */
  export const getDynamicName = (args?: Arguments): string => {
    if (args?.targetKind === 'relative') {
      return 'Relative Waypoint';
    }

    return name;
  };

  export const Arguments = zod.object({
    argumentKind: zod.literal('Waypoint'),
    shouldMatchJointAngles: zod.boolean().default(false),
    shouldMatchDynamicBasePosition: zod.boolean().default(false),
    motionKind: MotionKind.default('joint'),
    motionKindRaw: MotionKind.default('joint'),
    motionSpeed: zod.preprocess(
      (val) => (val == null ? undefined : val),
      MotionSpeed.optional(),
    ),
    speedLimitOption: SpeedLimitOption.default('PARENT_DEFAULTS'),
    positionListID: zod.string().nullable().default(null),
    positionListIndex: zod.number().optional(),
    target: Space.Position.nullable().default(null),
    tcpOption: TCPOptionOrAuto.default('auto'),
    gripperID: zod.string().optional(),
    expression: Expression.optional(),
    targetKind: zod
      .union([
        zod.literal('singlePosition'),
        zod.literal('positionList'),
        zod.literal('expression'),
        zod.literal('relative'),
      ])
      .default('singlePosition'),
    stopHere: zod.boolean().default(false),
    stopHereRaw: zod.boolean().default(false),
    blendConfig: BlendConfig.default({ kind: 'blendRadius', radius: 0 }),
    useParentBlendConfig: zod.boolean().default(true),
    moveDynamicBaseToReachPosition: zod.boolean().default(true),
    distanceUnit: DistanceUnit.default('meter'),
    palletConfig: zod
      .object({
        selectedLayer: Expression.optional(),
        selectedBox: Expression.optional(),
        selectedBoxType: zod.string().optional(),
        selectedPalletBaseID: zod.string().optional(),
        moveType: zod
          .union([
            zod.literal('box'),
            zod.literal('approach'),
            zod.literal('pickup'),
          ])
          .default('box'),
      })
      .default({
        selectedLayer: undefined,
        selectedBox: undefined,
        selectedBoxType: undefined,
        selectedPalletBaseID: undefined,
        moveType: 'box',
      }),
    relativeConfig: zod
      .object({
        targetKind: zod
          .union([
            zod.literal('singlePosition'),
            zod.literal('positionList'),
            zod.literal('expression'),
          ])
          .default('singlePosition'),
        frame: zod.string().default('base'),
        offsetX: Expression.optional(),
        offsetY: Expression.optional(),
        offsetZ: Expression.optional(),
        offsetRx: Expression.optional(),
        offsetRy: Expression.optional(),
        offsetRz: Expression.optional(),
      })
      .default({
        targetKind: 'singlePosition',
        frame: 'base',
        offsetX: undefined,
        offsetY: undefined,
        offsetZ: undefined,
        offsetRx: undefined,
        offsetRy: undefined,
        offsetRz: undefined,
      }),
    stopEarlyConditions: zod.string().default(''),
    useDeterministicPlanner: zod.boolean().optional(),
  });

  export type Arguments = zod.infer<typeof Arguments>;

  export const validator: Step.Validator = ({
    step,
    stepConfiguration,
    routine: { space },
    globalSpace,
    equipment,
    featureFlags,
  }) => {
    const spaceItems = [...globalSpace, ...space];
    const args = stepConfiguration?.args;

    if (args?.argumentKind !== argumentKind) {
      return;
    }

    // "Maintain robot configuration" (the deterministic planner) pins motion to
    // a single Elastokin IK branch, so it cannot run when the Elastokin IK
    // solver is disabled. Block the routine rather than silently ignoring it.
    if (args.useDeterministicPlanner && featureFlags?.disableElastokinIK) {
      throw new ActionRequiredError({
        kind: 'invalidConfiguration',
        message:
          '“Maintain robot configuration” requires the Elastokin IK solver, which is disabled (the disableElastokinIK feature flag is on). Turn off “Maintain robot configuration” or re-enable Elastokin IK to run this routine.',
        fieldId: 'useDeterministicPlanner',
      });
    }

    // Get the parent step kind
    const parentStepKind = step.parentSteps?.at(-1)?.stepKind ?? '';

    if (!permittedParentStepKinds.includes(parentStepKind)) {
      throw new ActionRequiredError({
        kind: 'invalidConfiguration',
        message:
          'Waypoint steps may only be used inside a Move Arm or Weld step',
      });
    }

    // Validate that for Weld steps, the tcpOption must be 'auto'
    if (parentStepKind === 'Weld' && args.tcpOption !== 'auto') {
      throw new ActionRequiredError({
        kind: 'invalidConfiguration',
        message:
          'TCP Option must be set to "auto" when used within a Weld step',
        fieldId: 'tcpOption',
      });
    }

    // checkValidPositionConfig

    const hasTarget = !!(args.target?.jointAngles || args.target?.pose);

    if (args.targetKind === 'singlePosition' && !hasTarget) {
      throw new ActionRequiredError({
        kind: 'invalidConfiguration',
        message: 'Joint angles or pose must be set',
        fieldId: 'jointAngles',
      });
    }

    // checkPositionListDefined

    if (args.targetKind === 'positionList') {
      if (!args.positionListID) {
        throw new ActionRequiredError({
          kind: 'invalidConfiguration',
          message: 'Position list must be set',
          fieldId: 'positionListID',
        });
      }

      const spaceItem = spaceItems.find((s) => s.id === args.positionListID);

      // Check if the referenced space item has unreachability warnings
      if (spaceItem?.warnings?.some((w) => w.kind === 'unreachable') === true) {
        throw new ActionRequiredError({
          kind: 'invalidConfiguration',
          message:
            'The referenced space item contains unreachable positions. Please update the positions before running.',
          fieldId: 'positionListID',
        });
      }

      if (spaceItem == null) {
        throw new ActionRequiredError({
          kind: 'invalidConfiguration',
          message: 'Choose a valid space item',
          fieldId: 'positionListID',
        });
      }

      // Check if gridPositionList has empty positions array
      if (
        spaceItem.kind === 'gridPositionList' &&
        (!spaceItem.positions || spaceItem.positions.length === 0)
      ) {
        throw new ActionRequiredError({
          kind: 'invalidConfiguration',
          message: `Generate a position list for ${spaceItem.name} before using it in a routine.`,
          fieldId: 'positionListID',
        });
      }

      if (
        args.positionListIndex != null && // If null, then cycling through list.
        spaceItem.kind !== 'visionPositionList' && // Skip validation for vision position lists as they're populated at runtime. (NOTE: space type has to be checked here due to this runtime population in order to allow for the "Always pick utilization". If no positions are available the "failed to generate motion plan" error will be output)
        (args.positionListIndex < 0 ||
          args.positionListIndex >= spaceItem.positions.length) // If index is out of bounds. (NOTE: Do not check the space type. This could be an issue if the space item is changed to a non-position list. Under the hood, RoutineFrame#getPositionListEntry can handle this, however, the user probably doesn't want to use an invalid/out-of-bounds index.)
      ) {
        throw new ActionRequiredError({
          kind: 'invalidConfiguration',
          message: 'Choose a valid index for the position list.',
          fieldId: 'positionListIndex',
        });
      }
    }

    if (args.targetKind === 'expression' && args.expression != null) {
      validateCodeExpression(
        args.expression,
        'expression',
        'Position expression',
      );
    }

    // Validate offset expressions
    validateCodeExpression(args.relativeConfig.offsetX, 'offsetX', 'X offset');
    validateCodeExpression(args.relativeConfig.offsetY, 'offsetY', 'Y offset');
    validateCodeExpression(args.relativeConfig.offsetZ, 'offsetZ', 'Z offset');

    validateCodeExpression(
      args.relativeConfig.offsetRx,
      'offsetRx',
      'Roll offset',
    );

    validateCodeExpression(
      args.relativeConfig.offsetRy,
      'offsetRy',
      'Pitch offset',
    );

    validateCodeExpression(
      args.relativeConfig.offsetRz,
      'offsetRz',
      'Yaw offset',
    );

    const hasAddOffsetParentStep = step.parentSteps.some(
      (parentStep) => parentStep?.stepKind === 'AddOffset',
    );

    if (hasAddOffsetParentStep && (args.shouldMatchJointAngles ?? false)) {
      throw new ActionRequiredError({
        kind: 'invalidConfiguration',
        message:
          'Waypoint steps inside an Add Offset step may not require exact final joint position, as only the tooltip pose can be guaranteed.',
        fieldId: 'shouldMatchJointAngles',
      });
    }

    // When parent is a weld step shouldMatchJointAngles can only be true for the first waypoint
    if (
      parentStepKind === 'Weld' &&
      step.siblingPosition !== 0 &&
      args.shouldMatchJointAngles
    ) {
      throw new ActionRequiredError({
        kind: 'invalidConfiguration',
        message:
          'Match Joint Angles can only be enabled for the first waypoint in a Weld step.',
        fieldId: 'shouldMatchJointAngles',
      });
    }

    // relative waypoint expression needs to be set
    if (
      args.targetKind === 'relative' &&
      args.positionListID === 'expression' &&
      !args.expression
    ) {
      throw new ActionRequiredError({
        kind: 'invalidConfiguration',
        message: 'Expression must be set',
        fieldId: 'expression',
      });
    }

    // Validate pallet configuration only when using palletBoxes space items
    if (
      (args.targetKind === 'positionList' || args.targetKind === 'relative') &&
      args.positionListID
    ) {
      const spaceItem = spaceItems.find((s) => s.id === args.positionListID);

      // Check if gridPositionList has empty positions array (for relative waypoints)
      if (
        spaceItem &&
        spaceItem.kind === 'gridPositionList' &&
        (!spaceItem.positions || spaceItem.positions.length === 0)
      ) {
        throw new ActionRequiredError({
          kind: 'invalidConfiguration',
          message: `Generate a position list for ${spaceItem.name} before using it in a routine.`,
          fieldId: 'positionListID',
        });
      }

      if (spaceItem?.kind === 'palletBoxes') {
        if (
          (args.palletConfig?.moveType === 'approach' ||
            args.palletConfig?.moveType === 'box') &&
          !args.palletConfig.selectedPalletBaseID
        ) {
          throw new ActionRequiredError({
            kind: 'invalidConfiguration',
            message:
              'Pallet base must be selected when using approach or box position',
            fieldId: 'palletConfig.selectedPalletBaseID',
          });
        }

        if (
          (args.palletConfig?.moveType === 'approach' ||
            args.palletConfig?.moveType === 'box') &&
          args.palletConfig.selectedPalletBaseID
        ) {
          const palletBase = spaceItems.find(
            (item) => item.id === args.palletConfig.selectedPalletBaseID,
          );

          if (!palletBase) {
            throw new ActionRequiredError({
              kind: 'invalidConfiguration',
              message: 'Pallet base not found',
              fieldId: 'palletConfig.selectedPalletBaseID',
            });
          }

          // Check if pallet base dimensions are too small for selected pallet boxes
          const isPalletBaseTooSmall = checkPalletBaseDimensions(
            spaceItem,
            palletBase as Space.PalletBase,
          );

          if (isPalletBaseTooSmall) {
            throw new ActionRequiredError({
              kind: 'invalidConfiguration',
              message:
                'Pallet base dimensions are too small for selected pallet boxes',
              fieldId: 'palletConfig.selectedPalletBaseID',
            });
          }
        }

        // Validate pickup position configuration
        if (
          args.palletConfig?.moveType === 'pickup' &&
          !args.palletConfig.selectedBoxType
        ) {
          throw new ActionRequiredError({
            kind: 'invalidConfiguration',
            message: 'Box type must be selected when using pickup position',
            fieldId: 'palletConfig.selectedBoxType',
          });
        }
      }
    }

    // Check if custom base is enabled and waypoint has match joint angles
    const hasCustomBase = equipment.some(
      (item) => item.config.kind === 'CustomBase' && item.isEnabled,
    );

    const allowCustomBaseMatchJointAngles = Boolean(
      featureFlags?.allowCustomBaseMatchJointAngles,
    );

    if (
      hasCustomBase &&
      args.shouldMatchJointAngles &&
      args.targetKind !== 'relative' &&
      !allowCustomBaseMatchJointAngles
    ) {
      throw new ActionRequiredError({
        kind: 'invalidConfiguration',
        message: 'Match Joint angles may not be used with a custom base',
        fieldId: 'shouldMatchJointAngles',
      });
    }

    // Check if dynamic base position matching is enabled but no dynamic base is available
    if (args.shouldMatchDynamicBasePosition) {
      const hasDynamicBase = equipment.some(
        (item) => isDynamicBase(item.config.kind) && item.isEnabled,
      );

      if (!hasDynamicBase) {
        throw new ActionRequiredError({
          kind: 'invalidConfiguration',
          message:
            'Match Dynamic Base Position may only be enabled when a dynamic base is available',
          fieldId: 'shouldMatchDynamicBasePosition',
        });
      }

      // Check if this is a relative waypoint
      if (args.targetKind === 'relative') {
        throw new ActionRequiredError({
          kind: 'invalidConfiguration',
          message:
            'Match Dynamic Base Position may not be enabled for relative waypoints',
          fieldId: 'shouldMatchDynamicBasePosition',
        });
      }
    }
  };

  export const toRoutineRunner: Step.ToRoutineRunner = ({
    stepConfiguration: { args },
    stepData,
    baseSpeedProfile,
  }) => {
    if (args?.argumentKind !== argumentKind) {
      throw new TypeError(`Expected argument kind ${argumentKind}`);
    }

    const {
      target,
      expression,
      targetKind,
      shouldMatchJointAngles,
      shouldMatchDynamicBasePosition,
      motionKind,
      motionSpeed,
      speedLimitOption,
      positionListID,
      positionListIndex,
      stopHere,
      blendConfig,
      useParentBlendConfig,
      tcpOption,
      gripperID,
      distanceUnit,
      relativeConfig,
      palletConfig,
      stopEarlyConditions,
      useDeterministicPlanner,
    } = args;

    validateMotionSpeed(name, motionSpeed);

    const speedProfile: SpeedProfile = calculateSpeedProfileFromStepMotionSpeed(
      {
        motionSpeed,
        baseSpeedProfile,
      },
    );

    const getTarget = (): WaypointStepArguments['target'] => {
      switch (targetKind) {
        case 'positionList':
          if (positionListID) {
            return {
              positionListID,
              positionListIndex,
            };
          }

          break;

        case 'expression':
          if (expression) {
            return { expression };
          }

          break;

        case 'relative':
          if (positionListID) {
            const targetReturn: any = {
              positionListID,
              positionListIndex,
            };

            // If this is a relative waypoint with expression, include the expression
            if (positionListID === 'expression' && expression) {
              targetReturn.expression = expression;
            }

            return targetReturn;
          }

          break;

        default:
          if (target) {
            return target;
          }
      }

      throw new ActionRequiredError({
        kind: 'invalidConfiguration',
        message: 'Target is not configured',
      });
    };

    // Cannot blend and stop at the same time - if stopping, don't use parent blend config
    const effectiveUseParentBlendConfig = stopHere
      ? false
      : useParentBlendConfig;

    // Force shouldMatchJointAngles to false for relative waypoints
    const effectiveShouldMatchJointAngles =
      targetKind === 'relative' ? false : shouldMatchJointAngles;

    // Force shouldMatchDynamicBasePosition to false for relative waypoints
    const effectiveShouldMatchDynamicBasePosition =
      targetKind === 'relative' ? false : shouldMatchDynamicBasePosition;

    // Only send relativeConfig when targetKind is 'relative', otherwise send nulled version
    const effectiveRelativeConfig =
      targetKind === 'relative'
        ? {
            ...relativeConfig,
            // Convert expressions to the format expected by routine-runner
            offsetX: relativeConfig.offsetX,
            offsetY: relativeConfig.offsetY,
            offsetZ: relativeConfig.offsetZ,
            offsetRx: relativeConfig.offsetRx,
            offsetRy: relativeConfig.offsetRy,
            offsetRz: relativeConfig.offsetRz,
          }
        : {
            targetKind: 'singlePosition' as const,
            frame: 'base',
            offsetX: undefined,
            offsetY: undefined,
            offsetZ: undefined,
            offsetRx: undefined,
            offsetRy: undefined,
            offsetRz: undefined,
          };

    const result = {
      ...stepData,
      stepKind: 'Waypoint' as const,
      args: {
        target: getTarget(),
        shouldMatchJointAngles: effectiveShouldMatchJointAngles,
        shouldMatchDynamicBasePosition: effectiveShouldMatchDynamicBasePosition,
        motionKind,
        stopHere,
        blendConfig,
        useParentBlendConfig: effectiveUseParentBlendConfig,
        motionSpeedPercent: motionSpeed?.motionSpeedPercent ?? null,
        speedProfile,
        useParentSpeedProfile: speedLimitOption === 'PARENT_DEFAULTS',
        tcpOption,
        gripperID,
        distanceUnit,
        relativeConfig: effectiveRelativeConfig,
        palletConfig,
        stopEarlyConditions: stopEarlyConditions
          ? buildConditional(JSON.parse(stopEarlyConditions))
          : null,
        useDeterministicPlanner,
      },
    };

    return result;
  };

  export const getStepDescription: Step.GetStepDescription = ({
    stepConfiguration: { args },
    routine,
    includeStepName,
    globalSpace,
  }) => {
    if (args?.argumentKind !== argumentKind || !routine) {
      return null;
    }

    // Helper function to prefix step name if required
    const prefixStepName = (description_: string) =>
      includeStepName ? `${name} ${description_}` : description_;

    if (args.targetKind === 'singlePosition') {
      // arbitrary specified point
      return prefixStepName('to manually specified position');
    }

    const allSpace = [...(globalSpace ?? []), ...routine.space];

    if (args.targetKind === 'positionList') {
      const space = allSpace.find((item) => item.id === args.positionListID);

      if (!space) {
        return null;
      }

      if (space.kind === 'singlePosition') {
        return prefixStepName(`to ${space.name}`);
      }

      if (space.kind === 'safeHomePose') {
        return prefixStepName('to home pose');
      }

      // Pallet items
      if (space.kind === 'palletBoxes') {
        const moveType = args.palletConfig?.moveType ?? 'box';

        if (moveType === 'approach') {
          return prefixStepName(
            `moving to approach positions of ${space.name}`,
          );
        }

        if (moveType === 'box') {
          return prefixStepName(`moving to box positions of ${space.name}`);
        }

        if (moveType === 'pickup') {
          const selectedBoxTypeID = args.palletConfig?.selectedBoxType;

          if (!selectedBoxTypeID) {
            return prefixStepName(`pickup position of ${space.name}`);
          }

          const boxType = selectedBoxTypeID
            ? space.boxTypes.find((bt: any) => bt.id === selectedBoxTypeID)
            : undefined;

          const boxTypeName =
            boxType?.name ?? selectedBoxTypeID ?? 'selected box type';

          const pickupPositionName =
            boxType?.pickupPosition?.name ?? 'pickup position';

          return prefixStepName(
            `moving to ${pickupPositionName} of box type ${boxTypeName} of ${space.name}`,
          );
        }
      }

      if (
        space.kind === 'freeformPositionList' ||
        space.kind === 'gridPositionList'
      ) {
        if (args.positionListIndex == null) {
          return prefixStepName(`to the next position in ${space.name}`);
        }

        return prefixStepName(
          `to position ${args.positionListIndex + 1} in ${space.name}`,
        );
      }

      return null;
    }

    if (args.targetKind === 'expression') {
      if (args.expression == null) {
        return null;
      }

      if (args.expression.kind === 'JavaScript') {
        // This looks pretty gnarly for most expressions. Probably want to allow
        // the user to build up the expression using a DSL that we can desugar
        // into JS later.
        const jsExprStr = convertJavaScriptExpressionToUser(
          args.expression.expression,
          { isForStepDescription: true },
        );

        return prefixStepName(`based on expression ${jsExprStr}`);
      }

      if (args.expression.kind === 'Python') {
        const { code: pyExprStr } =
          convertPythonExpression.withRemoteControlVariables.toUserDescription(
            args.expression.expression,
          );

        return prefixStepName(`based on expression ${pyExprStr}`);
      }

      return null;
    }

    if (args.targetKind === 'relative') {
      // Get the target description based on the positionListID
      let targetDescription: string | null = null;

      // For relative waypoints, the target is determined by positionListID
      if (args.positionListID === 'expression') {
        // Expression target
        if (args.expression) {
          if (args.expression.kind === 'JavaScript') {
            const jsExprStr = convertJavaScriptExpressionToUser(
              args.expression.expression,
              { isForStepDescription: true },
            );

            targetDescription = `expression ${jsExprStr}`;
          } else if (args.expression.kind === 'Python') {
            const { code: pyExprStr } =
              convertPythonExpression.withRemoteControlVariables.toUserDescription(
                args.expression.expression,
              );

            targetDescription = `expression ${pyExprStr}`;
          }
        }
      } else if (args.positionListID === 'current-position') {
        // Current position target
        targetDescription = 'current position';
      } else if (args.positionListID) {
        // Check if it's a waypoint reference
        const allSteps = listAllSteps(routine.steps);

        const waypointStep = allSteps.find(
          (step) => step.id === args.positionListID,
        );

        if (waypointStep && waypointStep.stepKind === 'Waypoint') {
          // Get a simple description for the waypoint without causing circular dependency
          const waypointConfig = routine.stepConfigurations[waypointStep.id];
          let waypointDescription = '';

          if (
            waypointConfig?.args &&
            waypointConfig.args.argumentKind === 'Waypoint'
          ) {
            const waypointArgs = waypointConfig.args as WaypointStep.Arguments;

            if (waypointArgs.targetKind === 'singlePosition') {
              waypointDescription = 'manually specified position';
            } else if (
              waypointArgs.targetKind === 'positionList' &&
              waypointArgs.positionListID
            ) {
              const waypointSpace = allSpace.find(
                (item) => item.id === waypointArgs.positionListID,
              );

              if (waypointSpace) {
                if (waypointSpace.kind === 'singlePosition') {
                  waypointDescription = waypointSpace.name;
                } else if (waypointSpace.kind === 'safeHomePose') {
                  waypointDescription = 'home pose';
                } else if (
                  waypointSpace.kind === 'freeformPositionList' ||
                  waypointSpace.kind === 'gridPositionList'
                ) {
                  waypointDescription = waypointSpace.name;
                }
              }
            } else if (
              waypointArgs.targetKind === 'expression' &&
              waypointArgs.expression
            ) {
              waypointDescription = 'expression';
            }
          }

          targetDescription = `waypoint ${waypointStep.stepPosition}${waypointDescription ? ` (${waypointDescription})` : ''}`;
        } else {
          // Position list target (space item)
          const space = allSpace.find(
            (item) => item.id === args.positionListID,
          );

          if (space) {
            if (space.kind === 'singlePosition') {
              targetDescription = space.name;
            } else if (space.kind === 'safeHomePose') {
              targetDescription = 'home pose';
            } else if (
              space.kind === 'freeformPositionList' ||
              space.kind === 'gridPositionList'
            ) {
              if (args.positionListIndex == null) {
                targetDescription = `the next position in ${space.name}`;
              } else {
                targetDescription = `position ${args.positionListIndex + 1} in ${space.name}`;
              }
            }
          }
        }
      }

      if (!targetDescription) {
        return null;
      }

      // Check if there are any offsets
      const { offsetX, offsetY, offsetZ, offsetRx, offsetRy, offsetRz } =
        args.relativeConfig;

      const hasOffsets =
        offsetX != null ||
        offsetY != null ||
        offsetZ != null ||
        offsetRx != null ||
        offsetRy != null ||
        offsetRz != null;

      if (!hasOffsets) {
        return prefixStepName(`to ${targetDescription}`);
      }

      // Format the offsets
      const offsetParts: string[] = [];

      // Use the distanceUnit from the configuration
      const unitInfo = getDistanceUnitInfo(args.distanceUnit);

      // Helper function to format expression offsets
      const formatExpressionOffset = (
        expr?: Expression,
        axis?: string,
        isRotation = false,
      ) => {
        if (expr == null) return null;

        // only handle JS+python expressions for now
        if (expr.kind !== 'JavaScript' && expr.kind !== 'Python') {
          return null;
        }

        // skip offsets of zero
        const value = parseFloat(expr.expression);
        if (value === 0) return null;

        let displayExpression = expr.expression;

        // Convert variable references to user-friendly names
        if (expr.kind === 'JavaScript') {
          displayExpression = convertJavaScriptExpressionToUser(
            expr.expression,
            { isForStepDescription: true },
          );
        } else if (expr.kind === 'Python') {
          const { code } =
            convertPythonExpression.withRemoteControlVariables.toUserDescription(
              expr.expression,
            );

          displayExpression = code;
        }

        if (isRotation) {
          return `${displayExpression} deg in ${axis}`;
        }

        return `${displayExpression} ${unitInfo.abbreviation} in ${axis}`;
      };

      // Add translation offsets
      const translationParts = [
        formatExpressionOffset(offsetX, 'X'),
        formatExpressionOffset(offsetY, 'Y'),
        formatExpressionOffset(offsetZ, 'Z'),
      ].filter((p): p is string => p != null);

      if (translationParts.length > 0) {
        offsetParts.push(...translationParts);
      }

      // Add rotation offsets
      const rotationParts = [
        formatExpressionOffset(offsetRx, 'Roll', true),
        formatExpressionOffset(offsetRy, 'Pitch', true),
        formatExpressionOffset(offsetRz, 'Yaw', true),
      ].filter((p): p is string => p != null);

      if (rotationParts.length > 0) {
        offsetParts.push(...rotationParts);
      }

      // Format with Oxford comma
      let offsetDescription: string;

      if (offsetParts.length === 0) {
        offsetDescription = '';
      } else if (offsetParts.length === 1) {
        [offsetDescription] = offsetParts;
      } else if (offsetParts.length === 2) {
        offsetDescription = `${offsetParts[0]} and ${offsetParts[1]}`;
      } else {
        const lastPart = offsetParts.pop();
        offsetDescription = `${offsetParts.join(', ')}, and ${lastPart}`;
      }

      return prefixStepName(
        `of ${offsetDescription} from ${targetDescription}`,
      );
    }

    return null;
  };
}

WaypointStep satisfies Step.StepKindInfo;
